import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Check, Close, Create, FileCopy, PictureAsPdf, Save } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataList from "components/DataList";
import { useSnackbar } from "notistack";
import { makeStyles } from "@mui/styles";
import getAllForms from "api/llm/getAllForms";
import { generateFilledPdfForApplicant } from "api/llm";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAppContextController } from "context/AppContext";
import FormRenderer from "layouts/pages/setup/components/DynamicForm/components/FormRenderer";
import updateApplicant from "layouts/pages/applicants/actions/updateApplicant";
import { useRefreshApplicant } from "hooks/useRefreshApplicant";
import moment from "moment";
import { getCompanyImageUrl } from "utils/helpers/upload";

const useStyles = makeStyles({
  addButton: {
    fontSize: 40,
    marginRight: 15,
  },
  dialogContent: {
    minHeight: "50vh",
    maxHeight: "70vh",
    overflowY: "auto",
  },
});

// API function to save form responses to applicant data
const saveFormResponsesToApplicant = async ({
  applicantId,
  currentApplicant,
  formId,
  shortName,
  responses,
  currentUser,
}) => {
  // Add submission date and user info to the form values
  const formData = {
    ...responses,
    submittedDate: new Date().toISOString(),
    completedBy:
      currentUser && currentUser.firstName && currentUser.lastName
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : "Unknown User",
    completedById: currentUser?._id || "",
  };

  // Create the dynamicForms structure with shortName as key, preserving existing data
  const data = {
    dynamicForms: {
      ...(currentApplicant.dynamicForms || {}), // Preserve existing forms
      [shortName]: formData, // Add/update the current form
    },
  };

  // Update the applicant with the new form data
  const result = await updateApplicant({
    applicantId,
    data,
    isFormSubmission: true,
  });

  return result;
};

const ApplicantForms = ({ currentApplicant }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [selectedForm, setSelectedForm] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isLoadingFormData, setIsLoadingFormData] = useState(false);
  const { company, currentLoggedUser, userType } = useAppContextController();
  const { refreshApplicant } = useRefreshApplicant();
  const IMAGE_SERVER = getCompanyImageUrl(company);

  // PDF viewer state
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);

  // Track form completion status
  const [completedForms, setCompletedForms] = useState({});

  const {
    data: allForms = [],
    isLoading: loading,
    error: formsError,
  } = useQuery(["dynamicForms"], getAllForms, {
    onError: (err) => {
      console.error("Error fetching forms:", err);
      enqueueSnackbar("Failed to load forms", { variant: "error" });
    },
  });

  // Filter to show only active forms
  const forms = React.useMemo(() => {
    if (!allForms || !Array.isArray(allForms)) return [];

    return allForms.filter((form) => {
      // Filter by active status
      const isActive = form.metadata?.status === "Active";

      // Filter by visibility based on userType
      let visibilityAllowed = true;
      if (form.metadata?.visibility === "AdminsOnly") {
        // Only Master and Admin can see AdminsOnly forms
        visibilityAllowed = userType === "Master" || userType === "Admin";
      } else if (form.metadata?.visibility === "Both") {
        // Everyone can see forms with visibility "Both"
        visibilityAllowed = true;
      }

      return isActive && visibilityAllowed;
    });
  }, [allForms, userType]);

  // Initialize completed forms based on applicant data
  React.useEffect(() => {
    if (currentApplicant) {
      const applicantForms = {};

      // Check for forms saved with shortName as key inside dynamicForms
      if (forms && forms.length > 0 && currentApplicant.dynamicForms) {
        forms.forEach((form) => {
          const shortName = form.metadata?.shortName;
          if (
            shortName &&
            currentApplicant.dynamicForms[shortName] &&
            currentApplicant.dynamicForms[shortName].submittedDate
          ) {
            applicantForms[form.id] = {
              completedOn: currentApplicant.dynamicForms[shortName].submittedDate,
              completedBy: currentApplicant.dynamicForms[shortName].completedBy || "Unknown User",
              completedById: currentApplicant.dynamicForms[shortName].completedById || "",
            };
          }
        });
      }

      // Also check the formResponses field for backward compatibility
      if (currentApplicant?.formResponses) {
        // Check if formResponses is an array or a single object
        const formResponsesArray = Array.isArray(currentApplicant.formResponses)
          ? currentApplicant.formResponses
          : [currentApplicant.formResponses];

        formResponsesArray.forEach((response) => {
          if (response.formId) {
            applicantForms[response.formId] = {
              completedOn: response.submittedDate,
              completedBy:
                response.completedBy ||
                `${currentApplicant.firstName} ${currentApplicant.lastName}`,
              completedById: response.completedById || "",
            };
          }
        });
      }

      setCompletedForms(applicantForms);
    }
  }, [currentApplicant, forms?.length]);

  const saveFormMutation = useMutation(saveFormResponsesToApplicant, {
    onSuccess: async (_, variables) => {
      enqueueSnackbar("Form submitted successfully", { variant: "success" });

      // Update completed forms
      setCompletedForms((prev) => ({
        ...prev,
        [selectedForm.id]: {
          completedOn: new Date().toISOString(),
          completedBy: `${currentLoggedUser?.firstName} ${currentLoggedUser?.lastName}`,
          completedById: currentLoggedUser?._id || "",
        },
      }));

      // Generate filled PDF and attach it to the applicant record
      try {
        console.log("Attempting to generate PDF for applicant with params:", {
          formId: selectedForm.id,
          applicantId: variables.applicantId || currentApplicant._id,
          formValuesCount: Object.keys(variables.responses || formValues).length,
        });

        await generateFilledPdfForApplicant({
          formId: selectedForm.id,
          applicantId: variables.applicantId || currentApplicant._id,
          formValues: variables.responses || formValues,
        });

        console.log("Successfully called PDF generation API");
        enqueueSnackbar("PDF successfully generated and attached to applicant record", {
          variant: "success",
        });
      } catch (pdfError) {
        console.error("Error generating filled PDF:", pdfError);
        enqueueSnackbar(`Error generating filled PDF: ${pdfError.message}`, { variant: "error" });
      }

      // Close the form dialog
      setIsFormDialogOpen(false);

      // Clear form values
      setFormValues({});

      // Refresh applicant data
      await refreshApplicant();

      // Invalidate queries to refresh the UI
      await queryClient.invalidateQueries(["applicants"]);
    },
    onError: (err) => {
      console.error("Error submitting form:", err);
      enqueueSnackbar(`Failed to submit form: ${err.message || "Unknown error"}`, {
        variant: "error",
      });
    },
  });

  const handleFillForm = async (form) => {
    try {
      setSelectedForm(form);
      setIsLoadingFormData(true);

      // In a real implementation, you might need to fetch the detailed form data
      // For now, we'll use the form data directly from the form object
      setIsFormDialogOpen(true);

      // Initialize form values object
      const initialValues = {};

      // First check if this form was saved with its shortName in dynamicForms
      const shortName = form.metadata?.shortName;
      if (shortName && currentApplicant.dynamicForms && currentApplicant.dynamicForms[shortName]) {
        // Filter out submittedDate from the saved values
        const { submittedDate, ...savedFormValues } = currentApplicant.dynamicForms[shortName];
        setFormValues(savedFormValues);
        setIsLoadingFormData(false);
        return;
      }

      // Check if this applicant already has responses in the formResponses field
      if (currentApplicant?.formResponses) {
        const formResponsesArray = Array.isArray(currentApplicant.formResponses)
          ? currentApplicant.formResponses
          : [currentApplicant.formResponses];

        const existingResponse = formResponsesArray.find((response) => response.formId === form.id);

        if (existingResponse?.responses) {
          // Use existing responses
          setFormValues(existingResponse.responses);
          // Skip further initialization as we're using saved values
          setIsLoadingFormData(false);
          return;
        }
      }

      // If no existing responses, initialize with defaults
      if (form.form && form.form.sections) {
        form.form.sections.forEach((section) => {
          if (section.rows) {
            section.rows.forEach((row) => {
              if (row.columns) {
                row.columns.forEach((field) => {
                  if (field.id) {
                    initialValues[field.id] = field.defaultValue || "";
                  }
                });
              }
            });
          }
        });
      }

      setFormValues(initialValues);
    } catch (error) {
      console.error("Error loading form data:", error);
      enqueueSnackbar("Failed to load form", { variant: "error" });
    } finally {
      setIsLoadingFormData(false);
    }
  };

  const handleCloseFormDialog = () => {
    setIsFormDialogOpen(false);
    setSelectedForm(null);
    setFormValues({});
  };

  const handleInputChange = (id, value) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    // Validate required fields
    let missingRequiredFields = false;

    if (selectedForm?.form?.sections) {
      selectedForm.form.sections.forEach((section) => {
        if (section.rows) {
          section.rows.forEach((row) => {
            if (row.columns) {
              row.columns.forEach((field) => {
                if (field.required && !formValues[field.id]) {
                  missingRequiredFields = true;
                }
              });
            }
          });
        }
      });
    }

    if (missingRequiredFields) {
      enqueueSnackbar("Please fill all required fields", { variant: "error" });
      return;
    }

    if (!currentApplicant?._id) {
      enqueueSnackbar("Applicant ID not found", { variant: "error" });
      return;
    }

    if (!currentLoggedUser?._id) {
      enqueueSnackbar("User information not available. Please log in again.", { variant: "error" });
      return;
    }

    // Get the form's short name
    const shortName = selectedForm.metadata?.shortName;

    await saveFormMutation.mutateAsync({
      applicantId: currentApplicant._id,
      currentApplicant,
      formId: selectedForm.id,
      shortName,
      responses: formValues,
      currentUser: currentLoggedUser,
    });
  };

  // Function to determine card border color based on completion status
  const getCardBorderColor = (form) => {
    const isCompleted = completedForms[form.id];
    if (isCompleted) {
      return "#4caf50"; // Green for completed forms
    }
    return "#f44336"; // Default color for incomplete forms
  };

  // Document type icon based on filename extension
  const getDocumentIcon = (filename) => {
    if (!filename) return null;

    const extension = filename?.split(".")?.pop()?.toLowerCase() || "";

    switch (extension) {
      case "pdf":
        return "https://cdn-icons-png.flaticon.com/512/337/337946.png";
      case "doc":
      case "docx":
        return "https://cdn-icons-png.flaticon.com/512/337/337932.png";
      case "xls":
      case "xlsx":
        return "https://cdn-icons-png.flaticon.com/512/337/337958.png";
      default:
        return "https://cdn-icons-png.flaticon.com/512/337/337956.png";
    }
  };

  // Find PDF attachment for form
  const findPdfAttachment = (form) => {
    if (!currentApplicant?.attachments?.length || !form?.metadata?.shortName) return null;

    return currentApplicant.attachments.find(
      (attachment) =>
        attachment.type === form.metadata.shortName && attachment.docType?.toLowerCase() === "pdf"
    );
  };

  // Handle PDF view
  const handleViewPdf = (form) => {
    const pdfAttachment = findPdfAttachment(form);
    if (pdfAttachment) {
      setSelectedPdf(pdfAttachment);
      setIsPdfDialogOpen(true);
    }
  };

  // Memoize the render function to prevent re-renders
  const renderRow = React.useCallback(
    (form) => {
      const isCompleted = completedForms[form.id];
      const hasPdf = !!findPdfAttachment(form);

      return (
        <Card
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderLeft: `4px solid ${getCardBorderColor(form)}`,
            backgroundColor: isCompleted ? "rgba(76, 175, 80, 0.05)" : "transparent",
            transition: "transform 0.3s, box-shadow 0.3s",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: 3,
            },
            m: 1,
          }}
        >
          <CardContent sx={{ padding: 2, flexGrow: 1 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CardMedia
                  component="img"
                  sx={{ width: 40, height: 40 }}
                  image={getDocumentIcon(form.originalFilename)}
                  alt="Document icon"
                />
                <Box>
                  <MDTypography variant="h6" component="div" fontWeight="bold">
                    {form.name}
                  </MDTypography>
                  <Typography variant="caption" color="text.secondary">
                    {moment(form.createdAt).format("MM-DD-YYYY")}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1}>
                {isCompleted ? (
                  <Chip
                    size="small"
                    icon={<Check style={{ color: "#ffffff" }} />}
                    label="Completed"
                    color="success"
                    sx={{ height: 24 }}
                    style={{ color: "#ffffff" }}
                  />
                ) : (
                  <Chip
                    size="small"
                    label="Incomplete"
                    color="error"
                    sx={{ height: 24 }}
                    style={{ color: "#ffffff" }}
                  />
                )}
              </Stack>
            </Box>

            <MDBox mt={2}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Short Name
                  </Typography>
                  <Typography variant="body2">{form.metadata?.shortName || "—"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Onboarding
                  </Typography>
                  <Typography variant="body2">{form.metadata?.onboarding || "—"}</Typography>
                </Grid>
              </Grid>
            </MDBox>

            {isCompleted && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Completed on: {moment(isCompleted.completedOn).format("MM-DD-YYYY")} by{" "}
                  {isCompleted.completedBy}
                </Typography>
              </Box>
            )}
          </CardContent>

          <CardActions sx={{ justifyContent: "flex-end", padding: 2, pt: 0 }}>
            {hasPdf && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PictureAsPdf />}
                onClick={() => handleViewPdf(form)}
                sx={{ mr: 1 }}
              >
                View PDF
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              color={isCompleted ? "success" : "primary"}
              startIcon={isCompleted ? <Check /> : <Create />}
              onClick={() => handleFillForm(form)}
            >
              {isCompleted ? "Review" : "Fill Form"}
            </Button>
          </CardActions>
        </Card>
      );
    },
    [completedForms, handleFillForm, findPdfAttachment, handleViewPdf]
  );

  const header = <></>;

  return (
    <>
      <DataList
        renderRow={renderRow}
        data={forms || []}
        onRowClick={() => {}}
        icon={<FileCopy />}
        iconColor="info"
        title="Forms"
        isLoading={loading}
        greybar={false}
        divider
        header={header}
        tableHeight={600}
        height={850}
        scrollY
        computedHeight
        emptyMessage="No active forms available"
      />

      <Dialog
        open={isFormDialogOpen}
        onClose={handleCloseFormDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            p: 2,
            width: "90%",
            maxWidth: "1200px",
          },
        }}
      >
        <DialogTitle>
          {selectedForm?.name}
          {isLoadingFormData && <LinearProgress />}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {isLoadingFormData ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <MDTypography variant="body2" color="text.secondary" mb={3}>
                Please fill out all required fields marked with an asterisk (*).
              </MDTypography>

              <Divider sx={{ mb: 3 }} />

              {selectedForm?.form && (
                <FormRenderer
                  formData={selectedForm.form}
                  formValues={formValues}
                  onInputChange={handleInputChange}
                  onSubmit={handleSubmitForm}
                  applicant={currentApplicant}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFormDialog} startIcon={<Close />}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitForm}
            variant="contained"
            color="primary"
            startIcon={<Save />}
            disabled={isLoadingFormData || saveFormMutation.isLoading}
          >
            {saveFormMutation.isLoading ? "Submitting..." : "Submit Form"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isPdfDialogOpen}
        onClose={() => setIsPdfDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            p: 2,
            width: "90%",
            maxWidth: "1200px",
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedPdf?.title ||
                `${currentApplicant?.firstName} ${currentApplicant?.lastName} - Form PDF`}
            </Typography>
            <IconButton onClick={() => setIsPdfDialogOpen(false)}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ minHeight: "70vh" }}>
          {selectedPdf ? (
            <object
              data={`${IMAGE_SERVER}/applicants/${currentApplicant?._id}/${selectedPdf.type}/${selectedPdf.filename}`}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ minHeight: "80vh" }}
            >
              <Typography>
                Unable to display PDF file.{" "}
                <a href={selectedPdf.presignedUrl} target="_blank" rel="noopener noreferrer">
                  Download
                </a>{" "}
                instead.
              </Typography>
            </object>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body1">No PDF selected or PDF not found.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

ApplicantForms.propTypes = {
  currentApplicant: PropTypes.object,
};

ApplicantForms.defaultProps = {
  currentApplicant: null,
};

export default ApplicantForms;
